import { FC, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { CompanyOption } from "@/components/CompanyOption/CompanyOption";
import { getCompanyKey, getSubdomain } from "@/core/utils/helpers";
import styles from "@/components/CompanyPickerDialog/CompanyPickerDialog.module.css";
import type { CompanyPickerDialogProps } from "@/components/CompanyPickerDialog/CompanyPickerDialog.types";

export const CompanyPickerDialog: FC<CompanyPickerDialogProps> = memo(
  ({ companies, currentCompanyKey, onValidate, onClose }) => {
    const { t } = useTranslation();
    const [selectedCompanyKey, setSelectedCompanyKey] =
      useState(currentCompanyKey);
    const selectedCompany = companies.find(
      (c) => getCompanyKey(c) === selectedCompanyKey,
    );
    const canValidate =
      selectedCompany !== undefined && selectedCompanyKey !== currentCompanyKey;

    return (
      <Dialog
        open
        modalType={onClose ? "modal" : "alert"}
        onOpenChange={() => onClose?.()}
      >
        <DialogSurface className={styles.surface}>
          <DialogBody>
            <DialogTitle
              action={
                onClose ? (
                  <Button
                    appearance="subtle"
                    icon={<Dismiss24Regular />}
                    aria-label={t("Close")}
                    onClick={onClose}
                  />
                ) : undefined
              }
            >
              {onClose ? t("Switch platform") : t("Choose your platform")}
            </DialogTitle>
            <DialogContent className={styles.content}>
              <p className={styles.description}>
                {t(
                  "Your account is linked to several Rise Up workspaces. Select the one you want to open.",
                )}
              </p>
              <div
                role="radiogroup"
                aria-label={t("Choose your platform")}
                className={styles.list}
              >
                {companies.map((company) => {
                  const companyKey = getCompanyKey(company);
                  return (
                    <CompanyOption
                      key={companyKey}
                      name={company.name}
                      subtitle={getSubdomain(
                        company.name,
                        company.custom,
                        company.apiDomain,
                      )}
                      selected={companyKey === selectedCompanyKey}
                      current={companyKey === currentCompanyKey}
                      onClick={() => setSelectedCompanyKey(companyKey)}
                    />
                  );
                })}
              </div>
            </DialogContent>
            <DialogActions>
              {onClose && (
                <Button appearance="secondary" onClick={onClose}>
                  {t("Cancel")}
                </Button>
              )}
              <Button
                appearance="primary"
                disabled={!canValidate}
                onClick={() => selectedCompany && onValidate(selectedCompany)}
              >
                {t("Validate")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  },
);
